import { ChangeDetectionStrategy, Component, inject } from '@angular/core';

import {
  FormsModule,
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import {
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatDialogModule,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';

export interface SaveAndApplySnapshotDialogData {
  snapshotName: string;
}

export interface SaveAndApplySnapshotDialogResult {
  action: 'save_and_apply' | 'cancel';
  newSnapshotName?: string;
  newSnapshotComment?: string;
}

@Component({
  selector: 'app-save-and-apply-snapshot-dialog',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatInputModule,
    MatFormFieldModule,
  ],
  templateUrl: './save-and-apply-snapshot-dialog.component.html',
  styleUrls: ['./save-and-apply-snapshot-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SaveAndApplySnapshotDialogComponent {
  private dialogRef = inject<MatDialogRef<SaveAndApplySnapshotDialogComponent>>(MatDialogRef);
  data = inject<SaveAndApplySnapshotDialogData>(MAT_DIALOG_DATA);
  private fb = inject(FormBuilder);

  snapshotForm: FormGroup;

  constructor() {
    this.snapshotForm = this.fb.group({
      name: ['', [Validators.required, this.minLengthValidator(1)]],
      comment: [''],
    });
  }

  onSaveAndApply(): void {
    if (this.snapshotForm.valid) {
      this.dialogRef.close({
        action: 'save_and_apply',
        newSnapshotName: this.snapshotForm.value.name,
        newSnapshotComment: this.snapshotForm.value.comment,
      } as SaveAndApplySnapshotDialogResult);
    }
  }

  onCancel(): void {
    this.dialogRef.close({
      action: 'cancel',
    } as SaveAndApplySnapshotDialogResult);
  }

  get isFormValid(): boolean {
    return this.snapshotForm.valid;
  }

  private minLengthValidator(requiredLength: number): ValidatorFn {
    return (control) => {
      const value = control.value ?? '';
      if (value.length < requiredLength) {
        return {
          minlength: {
            requiredLength,
            actualLength: value.length,
          },
        };
      }
      return null;
    };
  }
}
